import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Grid,
  Typography
} from '@mui/material';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import { styled } from '@mui/material/styles';
import Actions from 'components/Actions';
import AlertText from 'components/AlertText';
import Temporal from 'components/Temporal';
import {
  ROUTE_PATH as PourRoutePath,
  POUR_PARAM_IDENTIFIER as pourParamIdentifier,
  ROUTE_PARAM_IDENTIFIER as projectParamIdentifier,
  STOPE_PARAM_IDENTIFIER as stopeParamIdentifier
} from 'modules/project/PourDetailsPage/constants';
import {
  PourDto, PourStatus, ProjectDto, RheologyDataDto,
  UcsCoefficientsDto
} from 'providers/api';
import { Permission } from 'providers/authorisation';
import { indexBy, prop } from 'ramda';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SHORT_DATE_CONFIG, resolveTemplate } from 'utils';
import PourStatusDisplay from '../constants/pour';
import PourDetails from '../PourDetails';
import ListSkeleton from '../ProjectAreaPage/ProjectStopesPage/StopePanelPage/ListSkeleton';
import ProjectAuthorise from '../ProjectAuthorise';

const PREFIX = 'StopePourListView';

const classes = {
  root: `${PREFIX}-root`,
  headerTitle: `${PREFIX}-headerTitle`,
  alignRight: `${PREFIX}-alignRight`,
};

// fillHeight lets the list flow with the page (no inner scroll area) — used on the Stope Pour
// page; the scene side drawer keeps the default fixed-height scrolling container.
const Root = styled('div', { shouldForwardProp: (propName) => propName !== 'fillHeight' })<{ fillHeight?: boolean }>(({ fillHeight }) => ({
  [`&.${classes.root}`]: {
    height: fillHeight ? 'auto' : 'calc(100% - 20px)',
    overflowY: fillHeight ? 'visible' : 'auto',
  },

  [`& .${classes.headerTitle}`]: {
    maxWidth: '80%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  [`& .${classes.alignRight}`]: {
    textAlign: 'right',
  },
}));

interface StopePourListViewProps {
  backFillPlantNodeId?: string;
  project: ProjectDto;
  stopeId: string;
  rheologyDataSummaryListIsLoading: boolean;
  rheologyDataSummaryList: RheologyDataDto[];
  pourData: PourDto[];
  pourIdSelected: string | undefined;
  onPourSelect: (pour: PourDto | undefined) => void;
  onDeletePour?: (pour: PourDto) => void;
  fillHeight?: boolean;
}

const StopePourListView = ({ backFillPlantNodeId, project, stopeId, pourData, rheologyDataSummaryListIsLoading,
  rheologyDataSummaryList, pourIdSelected, onPourSelect, onDeletePour, fillHeight }: StopePourListViewProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [editing, setEditing] = useState<string | false>(false);

  const rheologyDataSummariesIndexedById = rheologyDataSummaryList ? indexBy(prop('rheologyDataSetId'), rheologyDataSummaryList) : {};

  const pours = pourData ? (
    project.isHideAbandonedPour
      ? pourData.filter((x) => x.status !== PourStatus.Abandoned)
      : pourData
  ).slice().reverse() : [];

  const collatedDisplayValues = (pour: PourDto) => {
    if (!rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? '']) {
      return {
        mixerCoefficients: undefined,
        specificGravity: undefined,
        heightOfCylinder: undefined,
        yieldStressA: undefined,
        yieldStressB: undefined,
        targetDaysCoefficients: undefined,
        tailingsDrySolidsDensity: undefined,
        carrierFluidDensity: undefined,
        binderDryParticleDensity: undefined,
        frictionLoss: undefined,
      };
    }

    const firstYieldStressCoefficient = rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].yieldStressCoefficients;

    return {
      mixerCoefficients: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].mixerCoefficients,
      specificGravity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].tailingsDrySolidsDensity,
      heightOfCylinder: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].heightOfCylinder,
      yieldStressA: firstYieldStressCoefficient?.coefficient1,
      yieldStressB: firstYieldStressCoefficient?.coefficient2,
      targetDaysCoefficients: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].ucsCoefficients,
      tailingsDrySolidsDensity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].tailingsDrySolidsDensity,
      carrierFluidDensity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].carrierFluidDensity,
      binderDryParticleDensity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].binderDryParticleDensity,
      frictionLoss: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].frictionLossFactorOfSafety,
    };
  };

  const handleChange = (pour: PourDto) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    onPourSelect(isExpanded ? pour : undefined);
    setExpanded(isExpanded ? pour.entityId : false);
    if (pour.entityId === editing) setEditing(false);
  };

  const handleViewPour = (pour: PourDto) => {
    navigate({
      pathname: resolveTemplate(PourRoutePath, {
        [projectParamIdentifier]: project.entityId,
        [stopeParamIdentifier]: stopeId,
        [pourParamIdentifier]: pour.entityId,
      }),
    });
  };

  useEffect(() => {
    if (pourIdSelected) {
      const pour = pourData.find((x) => x.entityId === pourIdSelected);
      if (pour) {
        onPourSelect(pour);
        setExpanded(pour.entityId);
        if (pour.entityId === editing) setEditing(false);
      }
    }
  }, [pourIdSelected]);

  if (rheologyDataSummaryListIsLoading) {
    return <ListSkeleton numberOfListItems={6} />;
  }

  if (!pours) {
    return <AlertText severity="info"><Trans id="No pours have been added.">No pours have been added.</Trans></AlertText>;
  }

  return (
    <Root className={classes.root} fillHeight={fillHeight}>
      {
        !rheologyDataSummaryListIsLoading && pours.map((pour, index) => (
          <Accordion key={pour.entityId} expanded={expanded === pour.entityId} onChange={handleChange(pour)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              id={`panel-${index}-header`}
            >
              <Grid container spacing={2} wrap="wrap">
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1" style={{ wordWrap: 'break-word' }}>{pour.reference}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{i18n._(PourStatusDisplay[pour.status])}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption">
                    {pour.intendedPourDate && (
                      <Temporal temporal={pour.intendedPourDate} options={DEFAULT_SHORT_DATE_CONFIG} />
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{
                width: 1,
              }}
              >
                {pour.isRockFill ? (
                  // Rock fill is a recipe-less, volume-only record: no recipe/rheology details and it can't be
                  // opened as a pour, so we show just its volume/height and omit the View action.
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      <Trans id="Rock fill — volume only, no recipe.">Rock fill — volume only, no recipe.</Trans>
                    </Typography>
                    <Typography variant="body2">{`${i18n._('Volume:')} ${Math.round(pour.volume)} m³`}</Typography>
                    {pour.pourHeight ? (
                      <Typography variant="body2">{`${i18n._('Height:')} ${pour.pourHeight.toFixed(1)} m`}</Typography>
                    ) : null}
                  </Box>
                ) : (
                  <>
                    <Typography variant="overline">{`${i18n._('Recipe:')} ${pour.recipeReference}`}</Typography>

                    {!rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? '']
                      && <Alert severity="error"><Trans id="Rheology data no longer available">Rheology data no longer available</Trans></Alert>}

                    <PourDetails
                      pour={pour}
                      displayUnitPreferences={project.displayUnitPreferences}
                      unitSystemPreference={project.unitSystemPreference}
                      customUnitFields={project.customUnitFields}
                      mixerCoefficients={collatedDisplayValues(pour).mixerCoefficients}
                      specificGravity={collatedDisplayValues(pour).specificGravity}
                      heightOfCylinder={collatedDisplayValues(pour).heightOfCylinder}
                      yieldStressA={collatedDisplayValues(pour).yieldStressA}
                      yieldStressB={collatedDisplayValues(pour).yieldStressB}
                      fillType={project.fillType}
                      tailingsSolidsDensity={collatedDisplayValues(pour).tailingsDrySolidsDensity}
                      carrierFluidDensity={collatedDisplayValues(pour).carrierFluidDensity}
                      binderParticleDensity={collatedDisplayValues(pour).binderDryParticleDensity}
                      throughputControlType={project.throughput}
                      targetDaysCoefficients={collatedDisplayValues(pour).targetDaysCoefficients as { [key: string]: UcsCoefficientsDto; }}
                      backFillPlantNodeId={backFillPlantNodeId}
                      pipeFrictionDataItems={pour.pipeFrictionDataItems}
                      pumpType={project.pumpType}
                    />
                  </>
                )}

                <Box sx={{
                  mt: 1,
                }}
                >
                  <Actions
                    right={[
                      ...(onDeletePour ? [[
                        'delete',
                        <ProjectAuthorise permission={Permission.ManageNodeData}>
                          <Button color="secondary" onClick={() => onDeletePour(pour)}>
                            <Trans id="Delete">Delete</Trans>
                          </Button>
                        </ProjectAuthorise>,
                      ] as [string, JSX.Element]] : []),
                      ...(pour.isRockFill ? [] : [[
                        'view',
                        <Button color="secondary" onClick={() => handleViewPour(pour)}>
                          <Trans id="View">View</Trans>
                        </Button>,
                      ] as [string, JSX.Element]]),
                    ]}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      }
    </Root>
  );
};

export default StopePourListView;
